<template>
  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
    <!-- Benodigde Rol -->
    <div class="sm:col-span-3">
      <label
        :for="`role-${index}`"
        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
      >
        Benodigde Rol
      </label>
      <input
        type="text"
        :id="`role-${index}`"
        :name="`job-function-requirement-${index}`"
        :value="modelValue.role"
        @input="updateField('role', $event.target.value)"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        data-lpignore="true"
        data-form-type="other"
        data-1p-ignore="true"
        data-np-ignore="true"
        readonly
        onfocus="this.removeAttribute('readonly')"
        :disabled="!canEdit"
        :class="[
          'block w-full rounded-lg border py-2 px-3 shadow-sm focus:outline-none sm:text-sm',
          !canEdit
            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent'
        ]"
        placeholder="Bijv. Project lead, Project medewerker"
      />
    </div>

    <!-- Team (Multiple) -->
    <div class="relative">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"> Team </label>
      <Listbox
        v-slot="{ open: teamOpen }"
        :model-value="modelValue.teams"
        @update:model-value="updateField('teams', $event)"
        multiple
        :disabled="!canEdit"
      >
        <div class="relative">
          <ListboxButton
            ref="teamButtonRef"
            :class="[
              'relative w-full rounded-lg border py-2 pl-3 pr-10 text-left focus:outline-none sm:text-sm',
              !canEdit
                ? 'cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400'
                : 'cursor-pointer border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              teamOpen && canEdit ? 'rounded-b-none border-b-gray-200 dark:border-b-gray-700' : ''
            ]"
          >
            <span
              v-if="modelValue.teams.length === 0"
              class="block truncate text-gray-400 dark:text-gray-500"
            >
              Selecteer team(s)...
            </span>
            <span v-else class="block truncate dark:text-white">
              {{ selectedTeamNames }}
            </span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400 dark:text-gray-500"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>
          <Teleport to="body">
            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                v-if="teamOpen"
                :style="teamDropdownStyle"
                class="z-[9999] rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 text-base shadow-lg focus:outline-none sm:text-sm max-h-[400px] overflow-auto"
              >
                <!-- Allemaal option -->
                <li
                  class="relative cursor-pointer select-none py-2 pl-10 pr-4 text-gray-900 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900/50 border-b border-gray-200 dark:border-gray-700"
                  @click.stop="toggleAllTeams"
                >
                  <span
                    :class="[
                      modelValue.teams.length === teams.length ? 'font-medium' : 'font-normal',
                      'block truncate'
                    ]"
                  >
                    Allemaal
                    <span class="text-gray-400 dark:text-gray-500 font-normal"
                      >({{ teams.length }})</span
                    >
                  </span>
                  <span
                    v-if="modelValue.teams.length === teams.length"
                    class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400"
                  >
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
                <ListboxOption
                  v-for="team in teams"
                  :key="team.simplicate_id"
                  :value="team.simplicate_id"
                  v-slot="{ active, selected }"
                  as="template"
                >
                  <li
                    :class="[
                      active
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                        : 'text-gray-900 dark:text-gray-200',
                      'relative cursor-pointer select-none py-2 pl-10 pr-4'
                    ]"
                  >
                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                      {{ team.name }}
                      <span class="text-gray-400 dark:text-gray-500 font-normal"
                        >({{ getTeamEmployeeCount(team.simplicate_id) }})</span
                      >
                    </span>
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400"
                    >
                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </Teleport>
        </div>
      </Listbox>
    </div>

    <!-- Ervaringsniveau (Multiple) -->
    <div class="relative">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Ervaringsniveau
      </label>
      <Listbox
        v-slot="{ open: levelOpen }"
        :model-value="modelValue.experienceLevels"
        @update:model-value="updateField('experienceLevels', $event)"
        multiple
        :disabled="!canEdit"
      >
        <div class="relative">
          <ListboxButton
            ref="levelButtonRef"
            :class="[
              'relative w-full rounded-lg border py-2 pl-3 pr-10 text-left focus:outline-none sm:text-sm',
              !canEdit
                ? 'cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400'
                : 'cursor-pointer border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              levelOpen && canEdit ? 'rounded-b-none border-b-gray-200 dark:border-b-gray-700' : ''
            ]"
          >
            <span
              v-if="modelValue.experienceLevels.length === 0"
              class="block truncate text-gray-400 dark:text-gray-500"
            >
              Selecteer niveau(s)...
            </span>
            <span v-else class="block truncate dark:text-white">
              {{ modelValue.experienceLevels.join(', ') }}
            </span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400 dark:text-gray-500"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>
          <Teleport to="body">
            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                v-if="levelOpen"
                :style="levelDropdownStyle"
                class="z-[9999] rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 text-base shadow-lg focus:outline-none sm:text-sm max-h-96 overflow-auto"
              >
                <!-- Allemaal option -->
                <li
                  v-if="filteredExperienceLevels.length > 0"
                  class="relative cursor-pointer select-none py-2 pl-10 pr-4 text-gray-900 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900/50 border-b border-gray-200 dark:border-gray-700"
                  @click.stop="toggleAllLevels"
                >
                  <span
                    :class="[
                      modelValue.experienceLevels.length === filteredExperienceLevels.length &&
                      filteredExperienceLevels.length > 0
                        ? 'font-medium'
                        : 'font-normal',
                      'block truncate'
                    ]"
                  >
                    Allemaal
                    <span class="text-gray-400 dark:text-gray-500 font-normal"
                      >({{ totalEmployeesInSelectedTeams }})</span
                    >
                  </span>
                  <span
                    v-if="
                      modelValue.experienceLevels.length === filteredExperienceLevels.length &&
                      filteredExperienceLevels.length > 0
                    "
                    class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400"
                  >
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
                <!-- Empty state when no levels available -->
                <li
                  v-if="filteredExperienceLevels.length === 0"
                  class="px-4 py-2 text-sm text-gray-500 dark:text-gray-400"
                >
                  Geen ervaringsniveaus beschikbaar
                </li>
                <ListboxOption
                  v-for="level in filteredExperienceLevels"
                  :key="level"
                  :value="level"
                  v-slot="{ active, selected }"
                  as="template"
                >
                  <li
                    :class="[
                      active
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                        : 'text-gray-900 dark:text-gray-200',
                      'relative cursor-pointer select-none py-2 pl-10 pr-4'
                    ]"
                  >
                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                      {{ level }}
                      <span class="text-gray-400 dark:text-gray-500 font-normal"
                        >({{ getLevelEmployeeCount(level) }})</span
                      >
                    </span>
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400"
                    >
                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </Teleport>
        </div>
      </Listbox>
    </div>

    <!-- DPW (Dagen per week) -->
    <div>
      <label
        :for="`dpw-${index}`"
        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
      >
        Dagen per week
      </label>
      <DpwInput
        :model-value="modelValue.dpw"
        @update:model-value="handleDpwUpdate"
        :disabled="!canEdit"
        :input-class="[
          'block w-full rounded-lg border py-2 pl-3 pr-3 text-left focus:outline-none sm:text-sm',
          !canEdit
            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            : isInvalid
              ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-800 dark:text-white'
              : 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 dark:text-white'
        ]"
      />
      <p v-if="isInvalid" class="mt-1 text-sm text-red-600 dark:text-red-400">
        Vul een geldig aantal dagen per week in
      </p>
    </div>

    <!-- Periode (Aangepaste datums) -->
    <div
      class="sm:col-span-3 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800"
    >
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          :checked="hasCustomDates"
          @change="toggleCustomDates($event)"
          :disabled="!canEdit"
          class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 disabled:opacity-50 dark:bg-gray-700"
        />
        <CalendarIcon class="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
          Aangepaste periode (afwijkend van project)
        </span>
      </label>

      <div v-if="hasCustomDates" class="mt-3 grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Van</label>
          <DatePickerLite
            :model-value="modelValue.startDate"
            @update:modelValue="val => updateField('startDate', val)"
            placeholder="Kies startdatum"
            :disabled="!canEdit"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tot</label>
          <DatePickerLite
            :model-value="modelValue.endDate"
            @update:modelValue="val => updateField('endDate', val)"
            placeholder="Kies einddatum"
            :disabled="!canEdit"
          />
        </div>
      </div>
    </div>

    <!-- Voorkeurscollega's -->
    <EmployeeMatchingSection
      :model-value="modelValue"
      :employees="employees"
      :can-edit="canEdit"
      :filtered-employee-list="filteredEmployeeList"
      :selected-employee-names="selectedEmployeeNames"
      :calculate-match-percentage="calculateMatchPercentage"
      :is-perfect-match="isPerfectMatch"
      @update:field="({ field, value }) => updateField(field, value)"
    />

    <!-- Notitie -->
    <div class="sm:col-span-3">
      <label
        :for="`notes-${index}`"
        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        Relevante context
      </label>
      <textarea
        :id="`notes-${index}`"
        :value="modelValue.notes"
        @input="updateField('notes', $event.target.value)"
        rows="3"
        :disabled="!canEdit"
        :class="[
          'mt-1 block w-full rounded-lg border py-2 px-3 shadow-sm focus:outline-none sm:text-sm',
          !canEdit
            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent'
        ]"
        placeholder="Benoem in ieder geval de context van de klus, of er flexibiliteit is in de startdatum, opbouw van dpw en/of senioriteit."
      ></textarea>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { CheckIcon, ChevronUpDownIcon, CalendarIcon } from '@heroicons/vue/24/outline'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { useElementBounding } from '@vueuse/core'
import DatePickerLite from '@/components/common/inputs/DatePickerLite.vue'
import DpwInput from '@/components/common/inputs/DpwInput.vue'
import EmployeeMatchingSection from '@/components/project-detail/EmployeeMatchingSection.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  teams: {
    type: Array,
    default: () => []
  },
  employees: {
    type: Array,
    default: () => []
  },
  experienceLevels: {
    type: Array,
    default: () => []
  },
  canEdit: {
    type: Boolean,
    default: true
  },
  isInvalid: {
    type: Boolean,
    default: false
  },
  prospect: {
    type: Object,
    default: null
  },
  calculateMatchPercentage: {
    type: Function,
    default: () => null
  },
  isPerfectMatch: {
    type: Function,
    default: () => false
  },
  getFilteredEmployees: {
    type: Function,
    default: null
  },
  getSelectedEmployeeNames: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['update:field', 'dpw-changed'])

const updateField = (field, value) => {
  emit('update:field', { field, value })
}

const handleDpwUpdate = value => {
  updateField('dpw', value)
  emit('dpw-changed', props.index)
}

// Dropdown button refs for Teleport positioning
const teamButtonRef = ref(null)
const levelButtonRef = ref(null)

// Track button positions for fixed dropdown positioning
const teamBounds = useElementBounding(teamButtonRef)
const levelBounds = useElementBounding(levelButtonRef)

// Computed styles for dropdown positioning (fixed, relative to viewport)
const teamDropdownStyle = computed(() => ({
  position: 'fixed',
  top: `${teamBounds.bottom.value}px`,
  left: `${teamBounds.left.value}px`,
  width: `${teamBounds.width.value}px`
}))

const levelDropdownStyle = computed(() => ({
  position: 'fixed',
  top: `${levelBounds.bottom.value}px`,
  left: `${levelBounds.left.value}px`,
  width: `${levelBounds.width.value}px`
}))

const selectedTeamNames = computed(() => {
  return props.modelValue.teams
    .map(tid => props.teams.find(t => t.simplicate_id === tid)?.name)
    .filter(Boolean)
    .join(', ')
})

const filteredEmployeeList = computed(() => {
  if (props.getFilteredEmployees) {
    return props.getFilteredEmployees(props.modelValue)
  }
  return props.employees
})

// Count employees per team (filtered by selected experience levels)
const getTeamEmployeeCount = teamId => {
  let filtered = props.employees.filter(emp => emp.teams?.some(t => t.simplicate_id === teamId))
  // If experience levels are selected, filter by those
  if (props.modelValue.experienceLevels?.length > 0) {
    filtered = filtered.filter(emp => props.modelValue.experienceLevels.includes(emp.function))
  }
  return filtered.length
}

// Count employees per experience level (filtered by selected teams)
const getLevelEmployeeCount = level => {
  let filtered = props.employees.filter(emp => emp.function === level)
  // If teams are selected, filter by those
  if (props.modelValue.teams?.length > 0) {
    filtered = filtered.filter(emp =>
      emp.teams?.some(t => props.modelValue.teams.includes(t.simplicate_id))
    )
  }
  return filtered.length
}

// Filter experience levels to only show those with employees in selected teams
const filteredExperienceLevels = computed(() => {
  // If no teams selected, show all experience levels
  if (!props.modelValue.teams?.length) {
    return props.experienceLevels
  }

  // Filter to only levels that have at least one employee in the selected teams
  return props.experienceLevels.filter(level => {
    return props.employees.some(
      emp =>
        emp.function === level &&
        emp.teams?.some(t => props.modelValue.teams.includes(t.simplicate_id))
    )
  })
})

// Total employee count for selected teams (used in "Allemaal" option for experience levels)
const totalEmployeesInSelectedTeams = computed(() => {
  if (!props.modelValue.teams?.length) {
    return props.employees.length
  }
  return props.employees.filter(emp =>
    emp.teams?.some(t => props.modelValue.teams.includes(t.simplicate_id))
  ).length
})

// Clear selected experience levels that are no longer available when teams change
watch(
  () => props.modelValue.teams,
  () => {
    if (props.modelValue.experienceLevels?.length > 0) {
      // Filter out experience levels that are no longer in the filtered list
      const validLevels = props.modelValue.experienceLevels.filter(level =>
        filteredExperienceLevels.value.includes(level)
      )
      if (validLevels.length !== props.modelValue.experienceLevels.length) {
        updateField('experienceLevels', validLevels)
      }
    }
  }
)

// Select/deselect all teams
const toggleAllTeams = () => {
  if (props.modelValue.teams.length === props.teams.length) {
    updateField('teams', [])
  } else {
    updateField(
      'teams',
      props.teams.map(t => t.simplicate_id)
    )
  }
}

// Select/deselect all experience levels
const toggleAllLevels = () => {
  if (props.modelValue.experienceLevels.length === filteredExperienceLevels.value.length) {
    updateField('experienceLevels', [])
  } else {
    updateField('experienceLevels', [...filteredExperienceLevels.value])
  }
}

const selectedEmployeeNames = computed(() => {
  if (props.getSelectedEmployeeNames) {
    return props
      .getSelectedEmployeeNames(props.modelValue.preferredEmployees, props.modelValue)
      .join(', ')
  }
  return props.modelValue.preferredEmployees
    .map(id => props.employees.find(e => e.simplicate_id === id)?.name)
    .filter(Boolean)
    .join(', ')
})

// Prospect date helpers
const prospectStartDate = computed(() => {
  if (!props.prospect?.start_date) return null
  return props.prospect.start_date.split('T')[0]
})

const prospectEndDate = computed(() => {
  if (!props.prospect?.expected_closing_date) return null
  return props.prospect.expected_closing_date.split('T')[0]
})

// Check if preference has custom dates set
const hasCustomDates = computed(() => {
  return !!(props.modelValue.startDate && props.modelValue.endDate)
})

// Toggle custom dates checkbox
const toggleCustomDates = event => {
  if (event.target.checked) {
    // Initialize with prospect dates - emit both fields together
    emit('update:field', {
      field: '__bulk__',
      value: { startDate: prospectStartDate.value, endDate: prospectEndDate.value }
    })
  } else {
    // Clear custom dates
    emit('update:field', { field: '__bulk__', value: { startDate: null, endDate: null } })
  }
}
</script>
